import env from 'env';
import { getNormalAuthToken } from 'api/authHeaders';
import { getAppLanguage } from 'utils/appLocale';

import docsLocales from '../docsLocales.json';

const DOCS_BASE_URL = env.REACT_APP_DOCS_BASE_URL || 'https://docs.tenzo.ai';

/** Hosted docs for the development app. */
export const DEV_DOCS_BASE_URL = 'https://tenzoai-main.mintlify.site';

const translatedPages = new Set(docsLocales.pages);

/** Use the active recruiter language only for pages with published translations. */
export function localizedDocsPath(
  path: string,
  language: string | null = getAppLanguage()
): string {
  const locale = language?.toLowerCase().split('-')[0];
  if (!locale || !docsLocales.languages.includes(locale)) return path;
  const match = /^\/?([^?#]*)([?#].*)?$/.exec(path);
  if (!match) return path;
  const [, page, suffix = ''] = match;
  if (!page || !translatedPages.has(page)) return path;
  return `/${locale}/${page}${suffix}`;
}

function joinDocsUrl(base: string, path: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  if (!path.startsWith('/')) {
    return `${base}/${path}`;
  }
  return `${base}${path}`;
}

export function docsUrl(path: string): string {
  return joinDocsUrl(DOCS_BASE_URL, localizedDocsPath(path));
}

export function devDocsUrl(path: string): string {
  return joinDocsUrl(DEV_DOCS_BASE_URL, localizedDocsPath(path));
}

/**
 * Open a docs path in a new tab. Production sets the docs session cookie first;
 * a failed cookie request still opens the page. The tab is reserved during the
 * click so the browser still allows it after the auth request.
 */
export async function openDocsPage(path: string): Promise<void> {
  const url = docsUrl(path);
  const token = env.ENV === 'production' ? getNormalAuthToken() : null;
  if (token === null) {
    window.open(url, '_blank');
    return;
  }

  const tab = window.open('about:blank', '_blank');
  try {
    const response = await fetch(`${DOCS_BASE_URL}/auth`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include',
    });
    if (!response.ok) {
      console.error('Failed to authenticate with docs service');
    }
  } catch (error) {
    // swallow-ok: a failed docs cookie still opens the page, which can ask the user to sign in
    console.error('Error opening docs:', error);
  }
  if (tab && !tab.closed) {
    tab.opener = null;
    tab.location.replace(url);
    return;
  }
  window.open(url, '_blank');
}

/** The development app opens product updates on the hosted dev docs. */
export async function openWhatsNewDocs(path: string): Promise<void> {
  if (env.ENV === 'development') {
    window.open(devDocsUrl(path), '_blank');
    return;
  }
  await openDocsPage(path);
}
